import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { NodeResult, RunDto } from "@custom-types/modelRun"
import { getNumberWithOrdinal } from "@features/results/const"
import { color_tones, lighter_rgba, quantileSorted, to_rgba } from "@features/results/Result/const"
import { StackedBarChartOptions } from "@features/results/Result/MultiNodeBarChartContainer/ChartOptionsDropdown"
import {
  MeanDataSetsType,
  MedianDataSetsType,
} from "@features/results/Result/MultiNodeBarChartContainer/MultiNodeBarChart"
import { ChartData, ChartDataset, ChartOptions } from "chart.js"
import { deviation, mean } from "d3"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { extractScenarioNameFromRun, getNodeRunsTimesteps, getRunNamesMap } from "utils/run"
import { generateChartLabel } from "@utils/chartLabelUtils"
import {
  Aggregation,
  NodeResultInScenario,
  NodeResultRow,
} from "@features/results/Result/ChartsContainer/NodeResultsTable"
import logger from "@utils/logger"
import { GroupNodeLabel } from "@features/graph/graphViewModel"

const COMPARISON_STACK_PREFIX = "do-not-stack-"

export const COMPARISON_AXIS_ID = "y-comparison"

export const isComparisonDataset = (dataset: ChartDataset<"bar", number[]>) =>
  Boolean(dataset.stack?.startsWith(COMPARISON_STACK_PREFIX))

const normalizeDatasetsToPercentages = (
  datasets: ChartDataset<"bar", number[]>[],
  labelCount: number,
) => {
  const totalsByTimestep = Array.from({ length: labelCount }, (_, timestepIndex) => {
    return datasets.reduce((sum, dataset) => {
      if (isComparisonDataset(dataset)) {
        return sum
      }

      const value = dataset.data[timestepIndex]
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return sum
      }

      return sum + value
    }, 0)
  })

  datasets.forEach((dataset) => {
    // comparison node can not be converted to a percentage, it keeps real units on a second axis
    if (isComparisonDataset(dataset)) {
      dataset.yAxisID = COMPARISON_AXIS_ID
      return
    }

    dataset.data = dataset.data.map((value, timestepIndex) => {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return value
      }

      const timestepTotal = totalsByTimestep[timestepIndex]
      if (!timestepTotal) {
        return 0
      }

      return (value / timestepTotal) * 100
    })
  })
}

export const getchartOptions = (
  format: string | undefined,
  legendPosition?: "top" | "bottom",
  showLegend?: boolean,
  onLegendClick?: (datasetLabel: string) => void,
  isStacked: boolean | undefined = true,
  showAsPercentage = false,
  hasComparison = false,
): ChartOptions<"bar"> => {
  return {
    responsive: true,
    scales: {
      x: { stacked: isStacked },
      y: {
        stacked: isStacked,
        max: showAsPercentage ? 100 : undefined,
        ticks: {
          callback: function (value: number | string) {
            if (showAsPercentage) {
              return `${value}%`
            }

            return formatValuePythonic(value, format)
          },
        },
      },
      ...(showAsPercentage &&
        hasComparison && {
          [COMPARISON_AXIS_ID]: {
            type: "linear" as const,
            position: "right" as const,
            // must stay false, otherwise comparison lines sum up instead of overlaying
            stacked: false,
            grid: { drawOnChartArea: false },
            ticks: {
              callback: function (value: number | string) {
                return formatValuePythonic(value, format)
              },
            },
          },
        }),
    },
    plugins: {
      tooltip: {
        caretSize: 0,
        enabled: true,
        mode: "index",
        callbacks: {
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset as ChartDataset<"bar", number[]>
            const value = Number(tooltipItem.raw || 0)
            // comparison node is never converted to a percentage
            const showRawUnits = !showAsPercentage || isComparisonDataset(dataset)

            const formattedValue = showRawUnits
              ? `${formatValuePythonic(value, format)}`
              : `${value.toFixed(2)}%`

            return `${dataset.label || ""}: ${formattedValue}`
          },
        },
      },
      legend: {
        position: legendPosition,
        display: showLegend ?? true,
        labels: {
          boxWidth: 11,
          boxHeight: 11,
          font: {
            size: 11,
          },
        },
        onClick: onLegendClick
          ? function (event, legendItem, legend) {
              const index = legendItem.datasetIndex
              if (index === undefined) return
              const dataset = legend.chart.data.datasets[index]
              if (dataset?.label) {
                onLegendClick(dataset.label)
              }
            }
          : undefined,
      },
      title: { display: false },
      zoom: {
        limits: { x: { minRange: 2 } },
        zoom: {
          drag: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
    },
  }
}

export const fillMissingTimesteps = <TDataSet extends MedianDataSetsType | MeanDataSetsType>(
  dataSets: TDataSet[],
  timesteps: string[],
): TDataSet[] => {
  return dataSets.map((dataSet) => {
    const filledMissingTimesteps = Object()

    Object.keys(dataSet).forEach((aggregate) => {
      filledMissingTimesteps[aggregate] = Object()

      timesteps.forEach((timestep) => {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        const value = dataSet[aggregate][timestep]
        if (value !== undefined) filledMissingTimesteps[aggregate][timestep] = value
        else filledMissingTimesteps[aggregate][timestep] = NaN
      })
    })

    return filledMissingTimesteps
  })
}

const getPercentileValue = (
  percentile: number | string | undefined,
  sliderValue: number,
): number => {
  return percentile === "custom" ? Number(sliderValue) : Number(percentile)
}

export const getMedianResults = (
  nodesRuns: NodeResult[][],
  maxLag: number,
  options: StackedBarChartOptions,
) => {
  const results: MedianDataSetsType[] = []

  const upperPercentileValue = getPercentileValue(
    options.percentileUpperValue,
    options.sliderUpperValue,
  )
  const lowerPercentileValue = getPercentileValue(
    options.percentileLowerValue,
    options.sliderLowerValue,
  )
  const upperPercentile = upperPercentileValue > 0 ? upperPercentileValue : 100
  const lowerPercentile = lowerPercentileValue > 0 ? lowerPercentileValue : 100

  nodesRuns.forEach((nodeData) => {
    const dataSetUpperPercentile: Record<string, number> = {}
    const dataSetMedian: Record<string, number> = {}
    const dataSetLowerPercentile: Record<string, number> = {}
    // guarantee data is orderly
    const sorted = Object.values(nodeData).sort((a, b) => (a.timestep > b.timestep ? 1 : -1))

    sorted.forEach(({ data, timestep }, index) => {
      if (index >= maxLag) {
        const percentile = quantileSorted(data || [], lowerPercentile / 100, upperPercentile / 100)

        dataSetLowerPercentile[timestep] = percentile[0]
        dataSetMedian[timestep] = percentile[1]
        dataSetUpperPercentile[timestep] = percentile[2]
      }
    })

    results.push({
      lower_percentile: dataSetLowerPercentile,
      median: dataSetMedian,
      upper_percentile: dataSetUpperPercentile,
    })
  })

  return results
}

export const getMeanResults = (
  nodesRuns: NodeResult[][],
  maxLag: number,
  showStDev: boolean | undefined,
) => {
  const results: MeanDataSetsType[] = []

  nodesRuns.forEach((nodeData) => {
    const result: MeanDataSetsType = {
      dev1_low: {},
      dev2_low: {},
      dev3_low: {},
      dev1_up: {},
      dev2_up: {},
      dev3_up: {},
      mean: {},
    }
    // guarantee data is orderly
    const sorted = Object.values(nodeData).sort((a, b) => (a.timestep > b.timestep ? 1 : -1))

    sorted.forEach(({ data, timestep }, index) => {
      if (index >= maxLag) {
        const meanVal = mean(data || []) || 0
        const stdev = deviation(data || []) || 0
        result.mean[timestep] = meanVal

        if (showStDev) {
          result.dev1_low[timestep] = meanVal - stdev
          result.dev2_low[timestep] = meanVal - 2 * stdev
          result.dev3_low[timestep] = meanVal - 3 * stdev
          result.dev1_up[timestep] = meanVal + stdev
          result.dev2_up[timestep] = meanVal + 2 * stdev
          result.dev3_up[timestep] = meanVal + 3 * stdev
        }
      }
    })
    results.push(result)
  })

  return results
}

const getMedianDataSets = (
  nodesRuns: NodeResult[][],
  compareToRun: NodeResult[] | undefined,
  maxLag: number,
  options: StackedBarChartOptions,
  timesteps: string[],
  customLabels: string[] = [],
  runs: RunDto[] = [],
) => {
  const dataSet: ChartDataset<"bar", number[]>[] = []

  const results = getMedianResults(nodesRuns, maxLag, options)
  const filledResults = fillMissingTimesteps(results, timesteps)

  let filledCompareRunResult: MedianDataSetsType[] | null = null
  if (compareToRun) {
    const compareResults = getMedianResults([compareToRun], maxLag, options)
    filledCompareRunResult = fillMissingTimesteps(compareResults, timesteps)
  }

  const percentileLowerValue = getPercentileValue(
    options.percentileLowerValue,
    options.sliderLowerValue,
  )
  const percentileUpperValue = getPercentileValue(
    options.percentileUpperValue,
    options.sliderUpperValue,
  )

  const runNamesMap = getRunNamesMap(runs)
  const numberOfRuns = runs.length

  filledResults.forEach((data, index) => {
    const firstResult = nodesRuns[index]?.[0]
    const nodeKey = firstResult?.node_key
    const customLabel = customLabels.find(
      (label) => graphViewGroupNodeLabelToSampleNode(label) === nodeKey,
    )
    const nodeName = customLabel ?? nodeKey ?? ""
    const nodeUnit = firstResult?.unit_format
    const currentRun = runs.find((run) => run.key === firstResult?.key)
    const runName = currentRun ? runNamesMap[currentRun.key] : undefined
    const runVersion = currentRun?.version

    const label = generateChartLabel({
      nodeName,
      nodeUnit,
      runName,
      runVersion,
      numberOfRuns,
    })

    dataSet.push({
      label,
      data: Object.values(data.median),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      borderColor: to_rgba(lighter_rgba(color_tones[index], 0, 1)),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      backgroundColor: to_rgba(lighter_rgba(color_tones[index], 0, 0.5)),
      order: 2,
    })
  })

  if (filledCompareRunResult?.[0]) {
    const firstCompareResult = compareToRun?.[0]
    const nodeKey = firstCompareResult?.node_key
    const customLabel = customLabels.find(
      (label) => graphViewGroupNodeLabelToSampleNode(label) === nodeKey,
    )
    const nodeName = customLabel ?? nodeKey ?? ""
    const nodeUnit = firstCompareResult?.unit_format
    const currentRun = runs.find((run) => run.key === firstCompareResult?.key)
    const runName = currentRun ? runNamesMap[currentRun.key] : undefined
    const runVersion = currentRun?.version
    const comparisonIndex = filledResults.length + 1

    if (percentileLowerValue > 0) {
      const label = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: `${getNumberWithOrdinal(percentileLowerValue)} percentile - comparison`,
      })
      dataSet.push({
        label,
        data: Object.values(filledCompareRunResult[0].lower_percentile),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
        borderWidth: 3,
        order: 1,
        borderDash: [10, 5],
        stack: "do-not-stack-lower-percentile-comparison",
        // @ts-expect-error valid value
        type: "line",
      })
    }

    const comparisonLabel = generateChartLabel({
      nodeName,
      nodeUnit,
      runName,
      runVersion,
      numberOfRuns,
      statisticalSuffix: "comparison",
    })
    dataSet.push({
      label: comparisonLabel,
      data: Object.values(filledCompareRunResult[0].median),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
      order: 1,
      stack: "do-not-stack-median-comparison",
      // @ts-expect-error valid value
      type: "line",
    })

    if (percentileUpperValue > 0) {
      const upperLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: `${getNumberWithOrdinal(percentileUpperValue)} percentile - comparison`,
      })
      dataSet.push({
        label: upperLabel,
        data: Object.values(filledCompareRunResult[0].upper_percentile),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
        borderWidth: 3,
        order: 1,
        borderDash: [3, 5],
        stack: "do-not-stack-upper-percentile-comparison",
        // @ts-expect-error valid value
        type: "line",
      })
    }
  }

  return dataSet
}

const getMeanDataSets = (
  nodesRuns: NodeResult[][],
  compareToRun: NodeResult[] | undefined,
  maxLag: number,
  timesteps: string[],
  customLabels: string[] = [],
  options: StackedBarChartOptions,
  runs: RunDto[] = [],
) => {
  const dataSet: ChartDataset<"bar", number[]>[] = []
  const results = getMeanResults(nodesRuns, maxLag, options.showStDev)
  const filledResults = fillMissingTimesteps(results, timesteps)

  let filledCompareRunResult: MeanDataSetsType[] | null = null
  if (compareToRun) {
    const compareResults = getMeanResults([compareToRun], maxLag, options.showStDev)
    filledCompareRunResult = fillMissingTimesteps(compareResults, timesteps)
  }

  const runNamesMap = getRunNamesMap(runs)
  const numberOfRuns = runs.length

  filledResults.forEach((data, index) => {
    const firstResult = nodesRuns[index]?.[0]
    const nodeKey = firstResult?.node_key
    const customLabel = customLabels.find(
      (label) => graphViewGroupNodeLabelToSampleNode(label) === nodeKey,
    )
    const nodeName = customLabel ?? nodeKey ?? ""
    const nodeUnit = firstResult?.unit_format
    const currentRun = runs.find((run) => run.key === firstResult?.key)
    const runName = currentRun ? runNamesMap[currentRun.key] : undefined
    const runVersion = currentRun?.version

    const label = generateChartLabel({
      nodeName,
      nodeUnit,
      runName,
      runVersion,
      numberOfRuns,
    })

    dataSet.push({
      label,
      data: Object.values(data.mean),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      borderColor: to_rgba(lighter_rgba(color_tones[index], 0, 1)),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      backgroundColor: to_rgba(lighter_rgba(color_tones[index], 0, 0.5)),
      order: 2,
    })
  })

  if (filledCompareRunResult?.[0]) {
    const firstCompareResult = compareToRun?.[0]
    const nodeKey = firstCompareResult?.node_key
    const customLabel = customLabels.find(
      (label) => graphViewGroupNodeLabelToSampleNode(label) === nodeKey,
    )
    const nodeName = customLabel ?? nodeKey ?? ""
    const nodeUnit = firstCompareResult?.unit_format
    const currentRun = runs.find((run) => run.key === firstCompareResult?.key)
    const runName = currentRun ? runNamesMap[currentRun.key] : undefined
    const runVersion = currentRun?.version
    const comparisonIndex = filledResults.length + 1

    if (options.showStDev && options.showStDev) {
      const dev3UpLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ+3σ - comparison",
      })
      dataSet.push({
        label: dev3UpLabel,
        data: Object.values(filledCompareRunResult[0].dev3_up),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.4)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.2)),
        borderDash: [12, 5],
        order: 1,
        stack: "do-not-stack-μ+3σ-comparison",
        // @ts-expect-error valid value
        type: "line",
      })

      const dev2UpLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ+2σ - comparison",
      })
      dataSet.push({
        label: dev2UpLabel,
        data: Object.values(filledCompareRunResult[0].dev2_up),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.5)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.3)),
        borderDash: [6, 4],
        stack: "do-not-stack-μ+2σ-comparison",
        order: 1,
        // @ts-expect-error valid value
        type: "line",
      })

      const dev1UpLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ+σ - comparison",
      })
      dataSet.push({
        label: dev1UpLabel,
        data: Object.values(filledCompareRunResult[0].dev1_up),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.7)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.5)),
        borderDash: [3, 3],
        stack: "do-not-stack-μ+σ-comparison",
        order: 1,
        // @ts-expect-error valid value
        type: "line",
      })
    }

    const meanCompLabel = generateChartLabel({
      nodeName,
      nodeUnit,
      runName,
      runVersion,
      numberOfRuns,
      statisticalSuffix: "comparison",
    })
    dataSet.push({
      label: meanCompLabel,
      data: Object.values(filledCompareRunResult[0].mean),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 1)),
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.5)),
      stack: "do-not-stack-mean-comparison",
      order: 1,
      // @ts-expect-error valid value
      type: "line",
    })

    if (options.showStDev) {
      const dev1LowLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ-σ - comparison",
      })
      dataSet.push({
        label: dev1LowLabel,
        data: Object.values(filledCompareRunResult[0].dev1_low),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.7)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.5)),
        borderDash: [3, 3],
        stack: "do-not-stack-μ-σ-comparison",
        order: 1,
        // @ts-expect-error valid value
        type: "line",
      })

      const dev2LowLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ-2σ - comparison",
      })
      dataSet.push({
        label: dev2LowLabel,
        data: Object.values(filledCompareRunResult[0].dev2_low),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.5)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.2)),
        borderDash: [6, 4],
        stack: "do-not-stack-μ-2σ-comparison",
        order: 1,
        // @ts-expect-error valid value
        type: "line",
      })

      const dev3LowLabel = generateChartLabel({
        nodeName,
        nodeUnit,
        runName,
        runVersion,
        numberOfRuns,
        statisticalSuffix: "μ-3σ - comparison",
      })
      dataSet.push({
        label: dev3LowLabel,
        data: Object.values(filledCompareRunResult[0].dev3_low),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.4)),
        borderWidth: 3,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[comparisonIndex], 0, 0.2)),
        borderDash: [12, 5],
        stack: "do-not-stack-μ-3σ-comparison",
        order: 1,
        // @ts-expect-error valid value
        type: "line",
      })
    }
  }

  return dataSet
}

export const getChartData = (
  nodesRuns: NodeResult[][],
  compareToRun: NodeResult[] | undefined,
  maxLag: number,
  options: StackedBarChartOptions,
  customLabels: string[] = [],
  runs: RunDto[] = [],
): ChartData<"bar", number[], string> => {
  const timesteps = getNodeRunsTimesteps(nodesRuns, maxLag)
  const datasets = options.isMean
    ? getMeanDataSets(nodesRuns, compareToRun, maxLag, timesteps, customLabels, options, runs)
    : getMedianDataSets(nodesRuns, compareToRun, maxLag, options, timesteps, customLabels, runs)

  if (options.showAsPercentage) {
    normalizeDatasetsToPercentages(datasets, timesteps.length)
  }

  return {
    labels: timesteps,
    datasets,
  }
}

export const getNodeTableRowsMean = (
  data: MeanDataSetsType[],
  runResults: NodeResult[][],
  compareToData: MeanDataSetsType | null,
  compareToResult: NodeResult[] | null,
  run: RunDto | undefined,
  nodes: GroupNodeLabel[],
  showStDev: boolean | undefined,
): NodeResultRow[] => {
  if (!run) return []

  const scenarioName = extractScenarioNameFromRun(run)
  const runNameWithoutScenario = (run.run_name || "").replace(`_${scenarioName}`, "")

  const nodeResults: NodeResultInScenario[] = []
  const rows: NodeResultRow[] = [
    {
      runName: runNameWithoutScenario,
      scenarios: [
        {
          name: scenarioName,
          nodes: nodeResults,
        },
      ],
    },
  ]

  for (let i = 0; i < runResults.length; i++) {
    const nodeKey = runResults[i]![0]?.node_key
    const node = nodes.find((n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey)

    if (!node) {
      logger.error(`Can not find corresponding node of results with node key "${nodeKey}"`)
      continue
    }

    let nodeResult = nodeResults.find((result) => result.node === node.label)
    if (!nodeResult) {
      nodeResult = {
        node: node.label,
        unitFormat: node?.format ?? "",
        aggregations: [],
      }
      nodeResults.push(nodeResult)
    }

    nodeResult.aggregations.push({
      name: "μ - unstacked",
      timestepValues: data[i]?.mean ?? {},
    })
  }

  if (compareToResult && compareToData) {
    const nodeKey = compareToResult[0]?.node_key
    const node = nodes.find((n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey)

    if (node) {
      const aggregations: Aggregation[] = []

      if (showStDev) {
        aggregations.push(
          { name: "μ+3σ", timestepValues: compareToData.dev3_up ?? {} },
          { name: "μ+2σ", timestepValues: compareToData.dev2_up ?? {} },
          { name: "μ+σ", timestepValues: compareToData.dev1_up ?? {} },
        )
      }

      aggregations.push({ name: "μ", timestepValues: compareToData.mean ?? {} })

      if (showStDev) {
        aggregations.push(
          { name: "μ-σ", timestepValues: compareToData.dev1_low ?? {} },
          { name: "μ-2σ", timestepValues: compareToData.dev2_low ?? {} },
          { name: "μ-3σ", timestepValues: compareToData.dev3_low ?? {} },
        )
      }

      nodeResults.push({
        node: node.label,
        unitFormat: node?.format ?? "",
        aggregations,
      })
    }
  }

  return rows
}

export const getNodeTableRowsMedian = (
  data: MedianDataSetsType[],
  runResults: NodeResult[][],
  yAxisUnitFormat: string | undefined,
  compareToData: MedianDataSetsType | null,
  compareToResult: NodeResult[] | null,
  run: RunDto | undefined,
  nodes: GroupNodeLabel[],
  percentiles: { lower: number; upper: number },
): NodeResultRow[] => {
  if (!run) return []

  const scenarioName = extractScenarioNameFromRun(run)
  const runNameWithoutScenario = (run.run_name || "").replace(`_${scenarioName}`, "")

  const nodeResults: NodeResultInScenario[] = []
  const rows: NodeResultRow[] = [
    {
      runName: runNameWithoutScenario,
      scenarios: [
        {
          name: scenarioName,
          nodes: nodeResults,
        },
      ],
    },
  ]

  for (let i = 0; i < runResults.length; i++) {
    const nodeKey = runResults[i]![0]?.node_key
    const node = nodes.find((n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey)

    if (!node) {
      logger.error(`Can not find corresponding node of results with node key "${nodeKey}"`)
      continue
    }

    let nodeResult = nodeResults.find((result) => result.node === node.label)
    if (!nodeResult) {
      nodeResult = {
        node: node.label,
        unitFormat: yAxisUnitFormat ?? "",
        aggregations: [],
      }
      nodeResults.push(nodeResult)
    }

    nodeResult.aggregations.push({
      name: "Median - unstacked",
      timestepValues: data[i]?.median ?? {},
    })
  }

  if (compareToResult && compareToData) {
    const nodeKey = compareToResult[0]?.node_key
    const node = nodes.find((n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey)

    if (node) {
      const aggregations: Aggregation[] = []

      if (percentiles.lower > 0) {
        aggregations.push({
          name: `${getNumberWithOrdinal(percentiles.lower)} percentile`,
          timestepValues: compareToData.lower_percentile ?? {},
        })
      }

      aggregations.push({
        name: "Median",
        timestepValues: compareToData.median ?? {},
      })

      if (percentiles.upper > 0) {
        aggregations.push({
          name: `${getNumberWithOrdinal(percentiles.upper)} percentile`,
          timestepValues: compareToData.upper_percentile ?? {},
        })
      }

      nodeResults.push({
        node: node.label,
        unitFormat: node.format ?? "",
        aggregations,
      })
    }
  }

  return rows
}
